import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BookOpen, Wrench, Sprout, Info, Mail, MapPin, GraduationCap, LayoutGrid } from 'lucide-react';
import ContactForm from './ContactForm';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const [isDarkBackground, setIsDarkBackground] = useState(false);

  // Special case for standalone pages (like registration)
  if (location.pathname === '/isee/registering') {
    return <>{children}</>;
  }

  useEffect(() => {
    const checkSystemTheme = () => {
      // Enhanced detection for Samsung Android devices
      let prefersDark = false;
      
      // Method 1: Standard prefers-color-scheme detection
      if (window.matchMedia) {
        prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      }
      
      // Method 2: Samsung-specific detection
      const userAgent = navigator.userAgent.toLowerCase();
      const isSamsung = /samsung|sm-/i.test(userAgent);
      const isAndroid = /android/i.test(userAgent);
      
      // Method 3: Check for Samsung Internet browser
      const isSamsungBrowser = /samsungbrowser/i.test(userAgent);
      
      // Method 4: Enhanced mobile detection with Samsung focus
      const isMobile = /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
      
      // Method 5: Check for Android dark mode indicators
      let androidDarkMode = false;
      if (isAndroid && window.matchMedia) {
        // Try multiple detection methods for Android
        androidDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
        
        // Additional check for Samsung devices
        if (isSamsung && !androidDarkMode) {
          // Samsung devices sometimes need additional detection
          try {
            // Check if the device supports dark mode at all
            const testMedia = window.matchMedia('(prefers-color-scheme: dark)');
            androidDarkMode = testMedia.matches;
          } catch (e) {
            console.log('Samsung dark mode detection failed:', e);
          }
        }
      }
      
      // Method 6: CSS-based detection as fallback
      let cssDarkMode = false;
      try {
        const testElement = document.createElement('div');
        testElement.style.colorScheme = 'dark';
        cssDarkMode = testElement.style.colorScheme === 'dark';
      } catch (e) {
        console.log('CSS dark mode detection failed:', e);
      }
      
      // Combine all detection methods
      const isDarkMode = prefersDark || androidDarkMode || (isSamsung && cssDarkMode);
      
      console.log('Enhanced theme detection:', {
        prefersDark,
        isSamsung,
        isAndroid,
        isSamsungBrowser,
        isMobile,
        androidDarkMode,
        cssDarkMode,
        finalResult: isDarkMode,
        userAgent: userAgent.substring(0, 100) // Log first 100 chars of UA
      });
      
      // Use detected theme
      setIsDarkBackground(isDarkMode);
    };

    // Initial check
    checkSystemTheme();
    
    // Listen for system theme changes with multiple methods
    const listeners: (() => void)[] = [];
    
    if (window.matchMedia) {
      // Standard media query listener
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => checkSystemTheme();
      
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleChange);
        listeners.push(() => mediaQuery.removeEventListener('change', handleChange));
      } else if (mediaQuery.addListener) {
        mediaQuery.addListener(handleChange);
        listeners.push(() => mediaQuery.removeListener(handleChange));
      }
    }
    
    // Additional listener for Samsung devices
    if (window.matchMedia) {
      const samsungQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const samsungHandler = () => {
        setTimeout(checkSystemTheme, 100); // Small delay for Samsung devices
      };
      
      if (samsungQuery.addEventListener) {
        samsungQuery.addEventListener('change', samsungHandler);
        listeners.push(() => samsungQuery.removeEventListener('change', samsungHandler));
      }
    }
    
    // Cleanup function
    return () => {
      listeners.forEach(cleanup => cleanup());
    };
  }, []);

  // `external: true` means the target is a hand-written static page under
  // public/, not a React route. Those must be plain <a> links — a react-router
  // <Link> would swallow the click, render the SPA fallback and never fetch the
  // real HTML file that Cloudflare serves at that path.
  const navigation = [
    { name: 'All', href: '/', icon: null, external: true },
    // The category tabs, server-rendered like `/` (lib/home/data.js).
    { name: 'Learn', href: '/learn', icon: GraduationCap, external: true },
    { name: 'Culture', href: '/culture', icon: BookOpen, external: true },
    { name: 'Tools', href: '/tools', icon: Wrench, external: true },
    { name: 'Growth', href: '/growth', icon: Sprout, external: true },
    { name: 'More', href: '/more', icon: LayoutGrid, external: true },
    { name: 'About', href: '/about', icon: Info, external: true },
  ];

  // The footer also keeps sitewide links to the sections the header no longer
  // names, so /isee/ and /sat/ don't lose the internal links they rank on.
  const footerLinks = [
    ...navigation,
    { name: 'ISEE & SSAT Prep', href: '/isee/', external: true },
    { name: 'SAT Prep', href: '/sat/', external: true },
    { name: 'Publishing', href: '/books', external: false },
    { name: 'Monthly Notes', href: '/blog', external: false },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`${isDarkBackground ? 'bg-gray-900' : 'bg-white'} shadow-sm border-b`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              {/* `/` is server-rendered (functions/index.js), not a React route. */}
              <a href="/" className="flex items-center space-x-2">
                <img 
                  src={isDarkBackground ? "/logo-light.png" : "/logo-dark.png"} 
                  alt="Woodlogos" 
                  className="w-26 h-11"
                />
              </a>
            </div>

            {/* Navigation */}
            <nav className="hidden md:flex space-x-8">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                const className = `flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? isDarkBackground
                      ? 'text-green-400 bg-green-900/20'
                      : 'text-green-600 bg-green-50'
                    : isDarkBackground
                      ? 'text-gray-300 hover:text-green-400 hover:bg-gray-800'
                      : 'text-gray-700 hover:text-green-600 hover:bg-gray-50'
                }`;
                const label = (
                  <>
                    {item.icon && <item.icon className="h-4 w-4" />}
                    <span>{item.name}</span>
                  </>
                );

                return item.external ? (
                  <a key={item.name} href={item.href} className={className}>
                    {label}
                  </a>
                ) : (
                  <Link key={item.name} to={item.href} className={className}>
                    {label}
                  </Link>
                );
              })}
            </nav>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                type="button"
                className="p-2 rounded-md text-gray-700 hover:text-green-600 hover:bg-gray-50"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-gray-50 border-t">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Company info */}
            <div>
              <div className="flex items-center mb-4">
                <img 
                  src="/logo-dark.png" 
                  alt="Woodlogos" 
                  className="w-19 h-7"
                />
              </div>
              <p className="text-gray-600 text-sm">
                Exploring and building in the AI-driven digital world, for ourselves
                and our clients, and connecting cultures through publishing.
              </p>
            </div>

            {/* Contact info */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Contact Us</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <div>
                  <span className="font-medium">Publishing & Cultural Exchange:</span>
                  <br />
                  <ContactForm 
                    inquiryType="books"
                    className="text-green-600 hover:text-green-700 inline-flex items-center"
                  >
                    <Mail className="h-4 w-4 mr-1" />
                    Contact Publishing Team
                  </ContactForm>
                </div>
                <div>
                  <span className="font-medium">AI & Digital:</span>
                  <br />
                  <ContactForm 
                    inquiryType="futurecode"
                    className="text-green-600 hover:text-green-700 inline-flex items-center"
                  >
                    <Mail className="h-4 w-4 mr-1" />
                    Contact Digital Team
                  </ContactForm>
                </div>
                <div>
                  <span className="font-medium">Office Address:</span>
                  <br />
                  <div className="flex items-center text-gray-600">
                    <MapPin className="h-4 w-4 mr-1 text-gray-500" />
                    <span>2108 N ST STE N Sacramento, CA, 95816</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick links */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Quick Links</h3>
              <div className="space-y-2">
                {footerLinks.map((item) =>
                  item.external ? (
                    <a
                      key={item.name}
                      href={item.href}
                      className="block text-sm text-gray-600 hover:text-green-600"
                    >
                      {item.name}
                    </a>
                  ) : (
                    <Link
                      key={item.name}
                      to={item.href}
                      className="block text-sm text-gray-600 hover:text-green-600"
                    >
                      {item.name}
                    </Link>
                  )
                )}
              </div>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-gray-200">
            <p className="text-center text-sm text-gray-500">
              © 2025 Woodlogos LLC. All rights reserved.
            </p>
            {/* Third-party trademark notices. Site-wide rather than only on the
                ISEE and SAT pages, because the main nav links to both sections
                from every page — the disclaimer belongs wherever the mark
                appears. Add a line here whenever a new exam section joins the
                nav above. */}
            <p className="mx-auto mt-3 max-w-2xl text-center text-xs leading-relaxed text-gray-400">
              ISEE® is a registered trademark of the Educational Records Bureau (ERB).
              SSAT® is a registered trademark of The Enrollment Management Association (EMA).
              Neither organization is affiliated with, sponsors, or endorses Woodlogos LLC or
              iSEE Vocab. All practice material on this site is independently written.
            </p>
            <p className="mx-auto mt-2 max-w-2xl text-center text-xs leading-relaxed text-gray-400">
              SAT® is a registered trademark of the College Board, which is not affiliated with,
              does not sponsor, and does not endorse Woodlogos LLC or SAT Prep. Bluebook™ is a
              trademark of the College Board. No retired or copyrighted College Board questions
              appear on this site.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
